import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Mail, Phone, Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API_URL } from '../../lib/api-client';

interface TeamMember {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  marketingData: {
    isActive: boolean;
    totalLeadsAssigned: number;
    totalCommissionsEarned: number;
  };
  createdAt: string;
}

interface TeamManagementProps {
  onUpdate: () => void;
}

export default function TeamManagement({ onUpdate }: TeamManagementProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/marketing-manager/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(data.teamMembers || []);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (memberId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/marketing-manager/team/${memberId}/status`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Team member ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchTeamMembers();
      onUpdate();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const viewMemberDetails = (member: TeamMember) => {
    setSelectedMember(member);
    setShowDetailsDialog(true);
  };

  const filteredMembers = teamMembers.filter(member =>
    `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-8">
          <div className="text-center text-gray-500">Loading team members...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader className="bg-white dark:bg-gray-800 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Manage and monitor your marketing team</CardDescription>
            </div>
            <Badge variant="outline" className="text-lg">
              {teamMembers.length} Members
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search */}
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />

            {/* Team Members List */}
            {filteredMembers.length > 0 ? (
              <div className="grid gap-4">
                {filteredMembers.map((member) => (
                  <div
                    key={member._id}
                    className="flex items-center justify-between p-4 border rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {member.firstName[0]}{member.lastName[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">
                          {member.firstName} {member.lastName}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {member.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {member.phone}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={member.marketingData.isActive ? 'default' : 'secondary'}>
                            {member.marketingData.isActive ? (
                              <><CheckCircle className="h-3 w-3 mr-1" /> Active</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1" /> Inactive</>
                            )}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {member.marketingData.totalLeadsAssigned} leads assigned
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-right mr-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Earned</p>
                        <p className="text-lg font-bold text-green-600">
                          ₹{(member.marketingData.totalCommissionsEarned || 0).toFixed(2)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewMemberDetails(member)}
                        className="shrink-0"
                      >
                        Details
                      </Button>
                      <Button
                        variant={member.marketingData.isActive ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => handleToggleActive(member._id, member.marketingData.isActive)}
                        className="shrink-0"
                      >
                        {member.marketingData.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No team members found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm ? 'Try adjusting your search' : 'You don\'t have any team members yet'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Member Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Team Member Details</DialogTitle>
            <DialogDescription>
              Detailed information about this team member
            </DialogDescription>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedMember.firstName[0]}{selectedMember.lastName[0]}
                </div>
                <div>
                  <h3 className="text-xl font-bold">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </h3>
                  <Badge variant={selectedMember.marketingData.isActive ? 'default' : 'secondary'}>
                    {selectedMember.marketingData.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                  <p className="font-medium">{selectedMember.email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                  <p className="font-medium">{selectedMember.phone}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Leads Assigned</p>
                  <p className="font-medium">{selectedMember.marketingData.totalLeadsAssigned}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Earned</p>
                  <p className="font-medium text-green-600">
                    ₹{(selectedMember.marketingData.totalCommissionsEarned || 0).toFixed(2)}
                  </p>
                </div>
                <div className="space-y-1 col-span-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Member Since</p>
                  <p className="font-medium">
                    {new Date(selectedMember.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}